import { useEffect, useState } from "react";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { BASE_URL } from "../../Config/api.config";

// Existing interfaces...
interface ServerInfo {
    server_time: string;
    php_version: string;
    memory_usage: string;
    memory_peak: string;
    memory_limit: string;
    max_execution_time: string;
    timezone: string;
    server_software: string;
    environment: string;
}

interface DatabaseInfo {
    table_count: number;
    database_size: string;
    status: string;
    connection_test: boolean;
}

interface PlatformStats {
    users: {
        total: number;
    };
    jobs: {
        active: number;
    };
    categories: {
        total: number;
    };
}

interface ApplicationMetrics {
    api_performance: {
        response_time_ms: number;
        status: string;
    };
    last_checked: string;
    uptime: string | null;
}

interface FileSystemInfo {
    uploads: {
        path: string;
        exists: boolean;
        file_count: number;
        size: string;
        is_writable: boolean;
        last_modified: string;
    };
    logs: {
        path: string;
        exists: boolean;
    };
    cache: {
        path: string;
        exists: boolean;
    };
    config: {
        path: string;
        exists: boolean;
    };
    disk_usage: {
        total: string;
        used: string;
        free: string;
        usage_percent: number;
        is_healthy: boolean;
    };
}

interface SystemStatus {
    status: string;
    result: {
        server_info: ServerInfo;
        database_info: DatabaseInfo;
        platform_stats: PlatformStats;
        application_metrics: ApplicationMetrics;
        file_system: FileSystemInfo;
        overall_status: string;
    };
}

// Updated File count result interface
interface FileCountResult {
    status: string;
    result: {
        directory: string;
        exists: boolean;
        is_readable: boolean;
        file_stats: {
            total_files: number;
            total_directories: number;
            files_by_type: Record<string, number>;
            files_by_extension: Record<string, number>;
            largest_file: {
                path: string;
                size: number;
                formatted_size: string;
            };
            recently_modified: Array<{
                path: string;
                modified: string;
                size: string;
            }>;
            by_directory: Record<string, number>;
        };
        last_modified: string;
        total_size: string;
    };
}

const HealthSystem = () => {
    const [adminData] = useLocalStorage("adminData", "");
    const [systemData, setSystemData] = useState<SystemStatus | null>(null);
    const [fileCountData, setFileCountData] = useState<FileCountResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [fileCountLoading, setFileCountLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fileCountError, setFileCountError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [directoryInput, setDirectoryInput] = useState("");
    const [showFileCount, setShowFileCount] = useState(false);
    const [activeTab, setActiveTab] = useState<'summary' | 'byType' | 'byExtension' | 'recent' | 'directories'>('summary');

    const fetchSystemHealth = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${BASE_URL}adm.system.health`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${adminData?.uid}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result: SystemStatus = await response.json();

            if (result?.status === "System health retrieved successfully") {
                setSystemData(result);
                setLastRefresh(new Date());
            } else {
                throw new Error(result?.status || "Failed to fetch system health");
            }
        } catch (err) {
            console.error("Error fetching system health:", err);
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const fetchFileCount = async (directory: string) => {
        try {
            setFileCountLoading(true);
            setFileCountError(null);
            setFileCountData(null);

            const response = await fetch(`${BASE_URL}adm.file-count?directory=${encodeURIComponent(directory)}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${adminData?.uid}`,
                },
            });

            const result = await response.json();

            if (result?.status === 'error') {
                const errorMsg = result?.result?.error_msg ||
                    result?.message ||
                    result?.status;
                throw new Error(errorMsg);
            }

            if (result?.status && result.status.includes("successfully")) {
                setFileCountData(result);
                setShowFileCount(true);
                setActiveTab('summary');
            } else {
                throw new Error(result?.status || "Failed to count files");
            }
        } catch (err) {
            console.error("Error counting files:", err);
            setFileCountError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setFileCountLoading(false);
        }
    };

    useEffect(() => {
        fetchSystemHealth();
    }, [adminData]);

    // Auto-refresh every 30 seconds if enabled
    useEffect(() => {
        if (!autoRefresh) return;

        const interval = setInterval(() => {
            fetchSystemHealth();
        }, 30000);

        return () => clearInterval(interval);
    }, [autoRefresh]);

    const formatBytes = (bytesString: string): string => {
        if (!bytesString) return "0 B";
        if (bytesString.includes(' ')) {
            return bytesString;
        }

        const bytes = parseFloat(bytesString);
        if (isNaN(bytes) || bytes === 0) return "0 B";

        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const getStatusColor = (status: string): string => {
        if (!status) return "bg-gray-100 text-gray-800 border-gray-200";

        switch (status.toLowerCase()) {
            case "healthy":
            case "connected":
            case "operational":
            case "success":
                return "bg-green-100 text-green-800 border-green-200";
            case "warning":
            case "partial":
            case "degraded":
                return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "error":
            case "critical":
            case "unavailable":
                return "bg-red-100 text-red-800 border-red-200";
            default:
                return "bg-gray-100 text-gray-800 border-gray-200";
        }
    };

    const handleFileCount = () => {
        if (directoryInput?.trim()) {
            fetchFileCount(directoryInput.trim());
        }
    };

    const getFileTypeColor = (fileType: string): string => {
        if (!fileType) return 'bg-gray-100 text-gray-800';

        switch (fileType.toLowerCase()) {
            case 'code': return 'bg-blue-100 text-blue-800';
            case 'image': return 'bg-green-100 text-green-800';
            case 'document': return 'bg-purple-100 text-purple-800';
            case 'archive': return 'bg-yellow-100 text-yellow-800';
            case 'video': return 'bg-red-100 text-red-800';
            case 'audio': return 'bg-indigo-100 text-indigo-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Function to get top directories
    const getTopDirectories = () => {
        if (!fileCountData?.result?.file_stats?.by_directory) return [];

        return Object.entries(fileCountData.result.file_stats.by_directory)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 20);
    };

    // Function to get top file types
    const getTopFileTypes = () => {
        if (!fileCountData?.result?.file_stats?.files_by_type) return [];

        return Object.entries(fileCountData.result.file_stats.files_by_type)
            .sort(([, a], [, b]) => b - a);
    };

    // Function to get top extensions
    const getTopExtensions = () => {
        if (!fileCountData?.result?.file_stats?.files_by_extension) return [];

        return Object.entries(fileCountData.result.file_stats.files_by_extension)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 20);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading system health...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Error Loading System Health
                    </h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={fetchSystemHealth}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!systemData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">No system health data available</p>
                </div>
            </div>
        );
    }

    const { server_info, database_info, platform_stats, application_metrics, file_system, overall_status } = systemData?.result || {};

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                System Health Dashboard
                            </h1>
                            <p className="text-gray-600">
                                Monitor your platform's performance and status
                            </p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-500">
                                Last updated: {lastRefresh?.toLocaleTimeString()}
                            </div>
                            <button
                                onClick={fetchSystemHealth}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Refresh</span>
                            </button>
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-600">Auto-refresh (30s)</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* File Counting Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">File System Analyzer</h3>
                    <div className="space-y-4">
                        <div className="flex space-x-4">
                            <input
                                type="text"
                                value={directoryInput}
                                onChange={(e) => setDirectoryInput(e.target.value)}
                                placeholder="Enter directory path (e.g., /var/www/uploads)"
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                                onClick={handleFileCount}
                                disabled={!directoryInput?.trim() || fileCountLoading}
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                {fileCountLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Counting...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Count Files</span>
                                    </>
                                )}
                            </button>
                            {showFileCount && (
                                <button
                                    onClick={() => setShowFileCount(false)}
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Hide Results
                                </button>
                            )}
                        </div>

                        {fileCountError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                {fileCountError}
                            </div>
                        )}

                        {fileCountLoading && (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                                <p className="mt-2 text-gray-600">Counting files in directory...</p>
                                <p className="text-sm text-gray-500">This may take a moment for large directories</p>
                            </div>
                        )}

                        {showFileCount && fileCountData?.result && (
                            <div className="mt-6 border-t pt-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900">
                                        File Analysis: <span className="text-blue-600">{fileCountData.result.directory}</span>
                                    </h4>
                                    <div className="text-sm text-gray-500">
                                        <span className="mr-4">Total Size: <span className="font-bold">{fileCountData.result.total_size}</span></span>
                                        <span>Last Modified: {fileCountData.result.last_modified}</span>
                                    </div>
                                </div>

                                {/* Summary Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl text-center">
                                        <div className="text-4xl font-bold text-blue-700">{fileCountData.result.file_stats?.total_files?.toLocaleString() || 0}</div>
                                        <div className="text-sm text-blue-600 font-medium mt-1">Total Files</div>
                                        <div className="text-xs text-blue-500 mt-2">
                                            {Math.round((fileCountData.result.file_stats?.total_files || 0) / ((fileCountData.result.file_stats?.total_files || 0) + (fileCountData.result.file_stats?.total_directories || 0)) * 100)}% of items
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl text-center">
                                        <div className="text-4xl font-bold text-purple-700">{fileCountData.result.file_stats?.total_directories?.toLocaleString() || 0}</div>
                                        <div className="text-sm text-purple-600 font-medium mt-1">Directories</div>
                                        <div className="text-xs text-purple-500 mt-2">
                                            {Math.round((fileCountData.result.file_stats?.total_directories || 0) / ((fileCountData.result.file_stats?.total_files || 0) + (fileCountData.result.file_stats?.total_directories || 0)) * 100)}% of items
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl text-center">
                                        <div className="text-4xl font-bold text-green-700">{fileCountData.result.total_size}</div>
                                        <div className="text-sm text-green-600 font-medium mt-1">Total Size</div>
                                        <div className="text-xs text-green-500 mt-2">
                                            Largest: {fileCountData.result.file_stats?.largest_file?.formatted_size || '0 B'}
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl text-center">
                                        <div className="text-4xl font-bold text-yellow-700">{Object.keys(fileCountData.result.file_stats?.files_by_type || {}).length}</div>
                                        <div className="text-sm text-yellow-600 font-medium mt-1">File Types</div>
                                        <div className="text-xs text-yellow-500 mt-2">
                                            {Object.keys(fileCountData.result.file_stats?.files_by_extension || {}).length} extensions
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs for different views */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-200">
                                        <nav className="-mb-px flex space-x-8">
                                            {['summary', 'byType', 'byExtension', 'recent', 'directories'].map((tab) => (
                                                <button
                                                    key={tab}
                                                    onClick={() => setActiveTab(tab as any)}
                                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab
                                                        ? 'border-blue-500 text-blue-600'
                                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                        }`}
                                                >
                                                    {tab === 'summary' && 'Summary'}
                                                    {tab === 'byType' && 'By Type'}
                                                    {tab === 'byExtension' && 'By Extension'}
                                                    {tab === 'recent' && 'Recently Modified'}
                                                    {tab === 'directories' && 'Directory Breakdown'}
                                                </button>
                                            ))}
                                        </nav>
                                    </div>
                                </div>

                                {/* Tab Content */}
                                <div className="mt-4">
                                    {/* Summary View */}
                                    {activeTab === 'summary' && (
                                        <div className="space-y-6">
                                            {/* File Types Chart */}
                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">File Type Distribution</h5>
                                                <div className="space-y-2">
                                                    {getTopFileTypes().map(([type, count]) => {
                                                        const percentage = Math.round((count / (fileCountData.result.file_stats?.total_files || 1)) * 100);
                                                        return (
                                                            <div key={type} className="flex items-center">
                                                                <div className="w-32">
                                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getFileTypeColor(type)}`}>
                                                                        {type?.charAt(0)?.toUpperCase() + type?.slice(1) || type}
                                                                    </span>
                                                                </div>
                                                                <div className="flex-1 ml-4">
                                                                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                                                                        <div
                                                                            className="h-full bg-blue-500"
                                                                            style={{ width: `${percentage}%` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                                <div className="w-24 text-right">
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        {count?.toLocaleString()}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 ml-2">
                                                                        ({percentage}%)
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Top 10 Extensions */}
                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Top 10 File Extensions</h5>
                                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                    {getTopExtensions().slice(0, 10).map(([ext, count]) => (
                                                        <div key={ext} className="bg-gray-50 p-3 rounded-lg text-center">
                                                            <div className="font-mono text-lg font-bold text-gray-900">.{ext}</div>
                                                            <div className="text-sm text-gray-600">{count?.toLocaleString()} files</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* By Type View */}
                                    {activeTab === 'byType' && (
                                        <div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {getTopFileTypes().map(([type, count]) => {
                                                    const percentage = Math.round((count / (fileCountData.result.file_stats?.total_files || 1)) * 100);
                                                    return (
                                                        <div key={type} className={`p-4 rounded-lg ${getFileTypeColor(type)} border`}>
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <div className="text-xl font-bold">{count?.toLocaleString()}</div>
                                                                    <div className="text-lg font-medium capitalize">{type} Files</div>
                                                                </div>
                                                                <div className="text-3xl">
                                                                    {type === 'code' && '💻'}
                                                                    {type === 'image' && '🖼️'}
                                                                    {type === 'document' && '📄'}
                                                                    {type === 'archive' && '🗜️'}
                                                                    {type === 'other' && '📁'}
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 text-sm">
                                                                <div className="flex justify-between mb-1">
                                                                    <span>Percentage</span>
                                                                    <span>{percentage}%</span>
                                                                </div>
                                                                <div className="h-2 bg-white/50 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-current opacity-50"
                                                                        style={{ width: `${percentage}%` }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* By Extension View */}
                                    {activeTab === 'byExtension' && (
                                        <div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead>
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Extension
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                File Count
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Percentage
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Type
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {getTopExtensions().map(([ext, count]) => {
                                                            const percentage = Math.round((count / (fileCountData.result.file_stats?.total_files || 1)) * 100);
                                                            const type = ext === 'php' || ext === 'js' || ext === 'css' || ext === 'html' ? 'code' :
                                                                ['jpg', 'png', 'gif', 'svg', 'webp'].includes(ext) ? 'image' :
                                                                    ['pdf', 'doc', 'docx', 'txt'].includes(ext) ? 'document' :
                                                                        ['zip', 'tar', 'gz'].includes(ext) ? 'archive' : 'other';
                                                            return (
                                                                <tr key={ext} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3">
                                                                        <span className="font-mono font-medium text-gray-900">.{ext}</span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className="font-medium">{count?.toLocaleString()}</span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center">
                                                                            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden mr-3">
                                                                                <div
                                                                                    className="h-full bg-blue-500"
                                                                                    style={{ width: `${percentage}%` }}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="text-sm text-gray-600">{percentage}%</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getFileTypeColor(type)}`}>
                                                                            {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Recently Modified View */}
                                    {activeTab === 'recent' && fileCountData.result.file_stats?.recently_modified?.length > 0 && (
                                        <div>
                                            <h5 className="font-medium text-gray-900 mb-3">Recently Modified Files (Last 24 Hours)</h5>
                                            <div className="space-y-2">
                                                {fileCountData.result.file_stats.recently_modified.map((file, index) => (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                                        <div className="flex-1 truncate">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {file?.path?.split('/')?.pop()}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {file?.path}
                                                            </div>
                                                        </div>
                                                        <div className="ml-4 text-right">
                                                            <div className="text-sm font-medium text-gray-900">{file?.modified}</div>
                                                            <div className="text-xs text-gray-500">{file?.size}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Directory Breakdown View */}
                                    {activeTab === 'directories' && (
                                        <div>
                                            <h5 className="font-medium text-gray-900 mb-3">Top Directories by File Count</h5>
                                            <div className="space-y-2">
                                                {getTopDirectories().map(([dir, count]) => (
                                                    <div key={dir} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                                        <div className="flex-1 truncate">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {dir?.split('/')?.pop() || dir}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {dir}
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                                                                {count?.toLocaleString()} files
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Largest File Info */}
                                {fileCountData.result.file_stats?.largest_file && (
                                    <div className="mt-8 pt-6 border-t">
                                        <h5 className="font-medium text-gray-900 mb-3">Largest File</h5>
                                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                                            <div className="flex items-start">
                                                <div className="mr-4 text-3xl">📦</div>
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-900 truncate">
                                                        {fileCountData.result.file_stats.largest_file.path?.split('/')?.pop()}
                                                    </div>
                                                    <div className="text-sm text-gray-600 truncate">
                                                        {fileCountData.result.file_stats.largest_file.path}
                                                    </div>
                                                    <div className="mt-2">
                                                        <span className="text-lg font-bold text-orange-600">
                                                            {fileCountData.result.file_stats.largest_file.formatted_size}
                                                        </span>
                                                        <span className="text-sm text-gray-500 ml-2">
                                                            ({fileCountData.result.file_stats.largest_file.size?.toLocaleString()} bytes)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Directory Information */}
                                <div className="mt-6 pt-6 border-t">
                                    <h5 className="font-medium text-gray-900 mb-3">Directory Information</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="text-center">
                                            <div className={`text-lg ${fileCountData.result.exists ? 'text-green-600' : 'text-red-600'}`}>
                                                {fileCountData.result.exists ? '✓ Exists' : '✗ Missing'}
                                            </div>
                                            <div className="text-xs text-gray-500">Exists</div>
                                        </div>
                                        <div className="text-center">
                                            <div className={`text-lg ${fileCountData.result.is_readable ? 'text-green-600' : 'text-red-600'}`}>
                                                {fileCountData.result.is_readable ? '✓ Readable' : '✗ Not Readable'}
                                            </div>
                                            <div className="text-xs text-gray-500">Permissions</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-medium text-gray-900">
                                                {fileCountData.result.last_modified?.split(' ')[0]}
                                            </div>
                                            <div className="text-xs text-gray-500">Modified Date</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-medium text-gray-900">
                                                {fileCountData.result.last_modified?.split(' ')[1]}
                                            </div>
                                            <div className="text-xs text-gray-500">Modified Time</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Directory Suggestions */}
                        <div className="text-sm text-gray-600">
                            <p className="mb-2">Quick directories to check:</p>
                            <div className="flex flex-wrap gap-2">
                                {file_system?.uploads?.exists && (
                                    <button
                                        onClick={() => setDirectoryInput(file_system.uploads.path)}
                                        className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                                    >
                                        Uploads ({file_system.uploads.file_count} files)
                                    </button>
                                )}
                                <button
                                    onClick={() => setDirectoryInput('.')}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                                >
                                    Current Directory (.)
                                </button>
                                <button
                                    onClick={() => setDirectoryInput('..')}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                                >
                                    Parent Directory (..)
                                </button>
                                <button
                                    onClick={() => setDirectoryInput('src')}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                                >
                                    src/
                                </button>
                                <button
                                    onClick={() => setDirectoryInput('uploads')}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                                >
                                    uploads/
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* File System Info from Health Check */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">File System Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Disk Usage */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-blue-800 font-medium">Disk Usage</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(file_system?.disk_usage?.is_healthy ? 'healthy' : 'warning')}`}>
                                    {file_system?.disk_usage?.is_healthy ? 'Healthy' : 'Warning'}
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-blue-900">{file_system?.disk_usage?.usage_percent || 0}%</div>
                            <div className="mt-2 text-sm text-blue-700">
                                {file_system?.disk_usage?.used || '0 B'} / {file_system?.disk_usage?.total || '0 B'}
                            </div>
                            <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-blue-600"
                                    style={{ width: `${Math.min(file_system?.disk_usage?.usage_percent || 0, 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Uploads Directory */}
                        <div className={`p-4 rounded-xl ${file_system?.uploads?.exists ? 'bg-gradient-to-br from-green-50 to-green-100' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <span className={`font-medium ${file_system?.uploads?.exists ? 'text-green-800' : 'text-gray-600'}`}>
                                    Uploads
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${file_system?.uploads?.exists ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {file_system?.uploads?.exists ? 'Exists' : 'Missing'}
                                </span>
                            </div>
                            {file_system?.uploads?.exists ? (
                                <>
                                    <div className="text-2xl font-bold text-green-900">{file_system.uploads.file_count} files</div>
                                    <div className="mt-2 text-sm text-green-700">
                                        Size: {file_system.uploads.size}
                                    </div>
                                    <div className="mt-2 text-xs text-green-600 truncate">
                                        {file_system.uploads.path}
                                    </div>
                                </>
                            ) : (
                                <div className="text-gray-500">Directory not found</div>
                            )}
                        </div>

                        {/* Logs Directory */}
                        <div className={`p-4 rounded-xl ${file_system?.logs?.exists ? 'bg-gradient-to-br from-yellow-50 to-yellow-100' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <span className={`font-medium ${file_system?.logs?.exists ? 'text-yellow-800' : 'text-gray-600'}`}>
                                    Logs
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${file_system?.logs?.exists ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {file_system?.logs?.exists ? 'Exists' : 'Missing'}
                                </span>
                            </div>
                            {file_system?.logs?.exists ? (
                                <div className="text-yellow-800">Log directory available</div>
                            ) : (
                                <div className="text-gray-500">Directory not found</div>
                            )}
                        </div>

                        {/* Config Directory */}
                        <div className={`p-4 rounded-xl ${file_system?.config?.exists ? 'bg-gradient-to-br from-purple-50 to-purple-100' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <span className={`font-medium ${file_system?.config?.exists ? 'text-purple-800' : 'text-gray-600'}`}>
                                    Config
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${file_system?.config?.exists ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {file_system?.config?.exists ? 'Exists' : 'Missing'}
                                </span>
                            </div>
                            {file_system?.config?.exists ? (
                                <div className="text-purple-800">Config directory available</div>
                            ) : (
                                <div className="text-gray-500">Directory not found</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Overall System Status */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Overall System Status</h2>
                            <p className="text-gray-600 mt-1">
                                Current health status of all system components
                            </p>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-lg font-medium ${getStatusColor(overall_status)}`}>
                            {overall_status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                    </div>
                </div>

                {/* System Status Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                    {/* Server Status */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Server Status</h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor("healthy")}`}>
                                Operational
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Server Time</span>
                                <span className="font-medium">{server_info?.server_time || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">PHP Version</span>
                                <span className="font-medium">{server_info?.php_version || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Environment</span>
                                <span className="font-medium capitalize">{server_info?.environment || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Web Server</span>
                                <span className="font-medium">{server_info?.server_software || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Timezone</span>
                                <span className="font-medium">{server_info?.timezone || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Database Status */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Database</h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(database_info?.status)}`}>
                                {database_info?.status || 'N/A'}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Tables</span>
                                <span className="font-medium">{database_info?.table_count || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Database Size</span>
                                <span className="font-medium">{database_info?.database_size || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Connection Test</span>
                                <span className={`font-medium ${database_info?.connection_test ? 'text-green-600' : 'text-red-600'}`}>
                                    {database_info?.connection_test ? '✓ Success' : '✗ Failed'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* API Health */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">API Health</h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application_metrics?.api_performance?.status)}`}>
                                {application_metrics?.api_performance?.status || 'N/A'}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Response Time</span>
                                <span className="font-medium">{application_metrics?.api_performance?.response_time_ms || 0}ms</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Last Checked</span>
                                <span className="font-medium">{application_metrics?.last_checked || 'N/A'}</span>
                            </div>
                            {application_metrics?.uptime && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">System Uptime</span>
                                    <span className="font-medium text-xs">{application_metrics.uptime}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Memory Usage */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Usage</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Memory Limit</span>
                                <span className="font-medium">{server_info?.memory_limit || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Memory Used</span>
                                <span className="font-medium">{server_info?.memory_usage || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Peak Memory</span>
                                <span className="font-medium">{server_info?.memory_peak || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Max Execution Time</span>
                                <span className="font-medium">{server_info?.max_execution_time || 'N/A'}s</span>
                            </div>
                        </div>
                    </div>

                    {/* Platform Stats */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Statistics</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Users</span>
                                <span className="font-medium">
                                    {platform_stats?.users?.total?.toLocaleString() || 0}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Active Jobs</span>
                                <span className="font-medium">
                                    {platform_stats?.jobs?.active?.toLocaleString() || 0}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Categories</span>
                                <span className="font-medium">
                                    {platform_stats?.categories?.total || 0}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Actions</h3>
                        <div className="space-y-3">
                            <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-left">
                                Clear Application Cache
                            </button>
                            <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-left">
                                Run Database Maintenance
                            </button>
                            <button className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-left">
                                View System Logs
                            </button>
                            <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-left">
                                Download Health Report
                            </button>
                        </div>
                    </div>
                </div>

                {/* System Information Summary */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">{platform_stats?.users?.total || 0}</div>
                            <div className="text-sm text-gray-600">Total Users</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">{platform_stats?.jobs?.active || 0}</div>
                            <div className="text-sm text-gray-600">Active Jobs</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">{database_info?.table_count || 0}</div>
                            <div className="text-sm text-gray-600">Database Tables</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600">{application_metrics?.api_performance?.response_time_ms || 0}ms</div>
                            <div className="text-sm text-gray-600">API Response Time</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HealthSystem;